import { Callsheet } from '__types__';
import {
  BooleanFilter,
  booleanFilterSchema
} from 'v5/api/_common/query/filters/BooleanFilter';
import {
  EnumFilter,
  enumFilterSchema
} from 'v5/api/_common/query/filters/EnumFilter';
import {
  scheduledFilter,
  ScheduledFilter
} from 'v5/core/standard-date-filter/scheduled-filter';
import { z } from 'zod';
import { paginationSchema, paginationUtils } from '../_common/query/pagination';
import {
  withBuilderUtils,
  WithFilters,
  withFiltersUtils,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import { WithWorkspace } from '../_common/query/workspace';
import { CreateCallsheetInput } from './callsheet.api.http.converter';
import {
  timestampFilter,
  TimestampFilter
} from 'v5/core/standard-date-filter/timestamp-filter';

/**
 * Callsheet search query with filters, sorting, and pagination
 */
export type CallsheetStatus = 'BUILDING' | 'SHARE' | 'PUBLISHED';

export const CALLSHEET_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'title',
  'fromDate',
  'status'
] as const;

export type CallsheetSortField = (typeof CALLSHEET_SORT_FIELDS)[number];

export type CallsheetTableColumnField = CallsheetSortField | 'owner';

export type CallsheetSearchQuery = WithSearch &
  WithPagination &
  WithSorter<CallsheetSortField> &
  WithFilters<{
    status: EnumFilter<CallsheetStatus>;
    archived: BooleanFilter;
    userId: EnumFilter<string>;
    productionIds: EnumFilter<number>;
    callsheetDate: ScheduledFilter;
    createdAt: TimestampFilter;
  }>;

/**
 * Callsheet API interface
 */
export interface CallsheetApi {
  searchCallsheets(
    query: WithWorkspace<CallsheetSearchQuery>
  ): Promise<{ items: Callsheet[]; count: number }>;

  getCallsheet(
    callsheetId: number,
    workspace: string
  ): Promise<Callsheet | null>;

  createCallsheet(
    workspace: string,
    request: CreateCallsheetInput
  ): Promise<Callsheet>;

  deleteCallsheet(callsheetId: number, workspace: string): Promise<void>;

  unarchiveCallsheet(
    callsheetId: number,
    workspace: string
  ): Promise<Callsheet>;
}

export const callsheetQueryUtils = withBuilderUtils({
  ...withSearchUtils<CallsheetSearchQuery>(),
  ...withFiltersUtils<CallsheetSearchQuery>(),
  ...withPaginationUtils<CallsheetSearchQuery>(),
  ...withSorterUtils<CallsheetSearchQuery>(),
  ofEmptySearchQuery: (): CallsheetSearchQuery => ({
    search: '',
    sort: { field: 'createdAt', order: 'desc' },
    page: paginationUtils.ofDefault(),
    filters: {
      status: null,
      archived: null,
      userId: null,
      productionIds: null,
      callsheetDate: null,
      createdAt: null
    }
  })
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

const strToNull = (str: unknown) => (str === '' ? null : str);

export const callsheetSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: z.object({
      status: z
        .preprocess(
          strToNull,
          enumFilterSchema(
            z.enum(['BUILDING', 'SHARE', 'PUBLISHED'])
          ).nullable()
        )
        .prefault(null),
      archived: z
        .preprocess(strToNull, booleanFilterSchema.nullable())
        .prefault(null),
      userId: z
        .preprocess(strToNull, enumFilterSchema(z.string()).nullable())
        .prefault(null),
      productionIds: z
        .preprocess(strToNull, enumFilterSchema(z.coerce.number()).nullable())
        .prefault(null),
      callsheetDate: z
        .preprocess(strToNull, scheduledFilter.schema.nullable())
        .prefault(null),
      createdAt: z
        .preprocess(strToNull, timestampFilter.schema.nullable())
        .prefault(null)
    }),
    page: paginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(CALLSHEET_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(() => callsheetQueryUtils.ofEmptySearchQuery());
